'use client';

import React from 'react';
import clsx from 'clsx';

import type { MenuCollection, MenuData, MenuSection, Template, TemplateNode, TemplateThemeMode } from '@/app/dashboard/templates/components/types/template';
import type { PagedFlipDocument, PagedFlipPage } from '@/lib/mnrq/paged-flip/types';
import {
  applyTextUtils,
  computeStyle,
  extendRepeaterContext,
  findAllRepeaters,
  inferRepeaterContextKey,
  injectBindings,
  isBindingMap,
  pickRenderableText,
  renderTextOrChildren,
  resolveAllBindings,
  resolveBindingExpression,
  resolveClassName,
  resolveClassNameBindings,
  resolvePath,
  resolveRenderableValue,
  resolveRepeaterCollection,
  splitColumns,
} from '@/app/dashboard/templates/components/lib/util';
import { getTemplatePagedNodes } from '@/app/dashboard/templates/components/lib/pagedFlipTemplate';
import { resolveImagePresentation, resolveCutoutSource, resolveDecorAssetUrl } from '@/app/dashboard/templates/components/lib/templateImage';
import { resolveGroupLayout } from '@/app/dashboard/templates/components/lib/templateLayout';
import { getDefaultPagedFlipSlotNodes } from './pagedFlipDefaultNodes';
import Image from 'next/image';
import Link from 'next/link';

type PagedNodeRenderOptions = {
  scales: {
    font: number;
    spacing: number;
    layout: number;
  };
  scaleTexts: boolean;
  isCompactSplitLayout?: boolean;
};

const TAILWIND_TEXT_SIZE_REM: Record<string, number> = {
  xs: 0.75,
  sm: 0.875,
  base: 1,
  lg: 1.125,
  xl: 1.25,
  '2xl': 1.5,
  '3xl': 1.875,
  '4xl': 2.25,
  '5xl': 3,
  '6xl': 3.75,
  '7xl': 4.5,
  '8xl': 6,
  '9xl': 8,
};

function parseTextSizeClass(className: string) {
  const tokens = className.split(/\s+/).filter(Boolean);
  let resolved: string | null = null;

  for (const token of tokens) {
    if (token.includes(':')) continue;
    const arbitrary = token.match(/^text-\[(.+)]$/);
    if (arbitrary?.[1]) {
      resolved = arbitrary[1];
      continue;
    }

    const named = token.match(/^text-(xs|sm|base|lg|xl|[2-9]xl)$/);
    if (named?.[1]) {
      resolved = `${TAILWIND_TEXT_SIZE_REM[named[1]]}rem`;
    }
  }

  return resolved;
}

function applyClassTextScale(
  style: React.CSSProperties,
  className: string,
  options: PagedNodeRenderOptions,
) {
  if (!options.scaleTexts || style.fontSize) return style;

  const classFontSize = parseTextSizeClass(className);
  if (!classFontSize) return style;

  return {
    ...style,
    fontSize: `calc(${classFontSize} * ${options.scales.font})`,
  };
}

function getPageKindLabel(kind: PagedFlipPage['kind']) {
  switch (kind) {
    case 'cover':
      return 'Cover';
    case 'toc':
      return 'Guide';
    case 'chapter':
      return 'Chapter';
    case 'section-divider':
      return 'Section';
    case 'category-spotlight':
      return 'Spotlight';
    case 'featured-showcase':
      return 'Featured';
    case 'new-arrivals':
      return 'New';
    case 'back-cover':
      return 'Back';
    default:
      return 'Page';
  }
}

function sanitizeElementProps(node: TemplateNode) {
  const elementProps: any = {
    key: node.id,
    id: node.props?.id,
    ...(node.props || {}),
  };

  [
    'repeater',
    'modal',
    'show',
    'animate',
    'onClick',
    'responsiveFont',
    'responsiveSpacing',
    'truncate',
    'ellipsis',
    'utils',
    'text',
    'children',
    'targetId',
    'bindings',
    'pageSlot',
    'structureId',
    'layout',
    'hotspot',
    'showInSlider',
    'imageBinding',
    'sliderNav',
    'sliderMain',
    'column',
    'promoStyle',
    'promoTypeStyles',
    'ifPromo',
    'ifPromoType',
    'splitItemsColumns',
    'splitCategoriesColumns',
    'availabilityOnly',
    'hideWhenUnavailable',
    'featuredOnly',
    'sectionId',
    'sectionIdBinding',
    'categoryIds',
    'categoryIdsBinding',
    'shape',
    'ring',
    'shadow',
    'cutout',
    'decor',
  ].forEach((key) => delete elementProps[key]);

  if (node.type === 'img') {
    ['src', 'key', 'alt'].forEach((key) => delete elementProps[key]);
  }

  return elementProps;
}

function collectPageItems(page: PagedFlipPage) {
  return (page.categories || []).flatMap((category) => category.items || []);
}

function normalizeImageSrc(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;

  const src = value.trim();
  if (!src) return undefined;
  if (/^(javascript|vbscript):/i.test(src)) return undefined;
  if (/^data:image\/svg\+xml/i.test(src)) return undefined;
  if (/\.svg(\?|#|$)/i.test(src)) return undefined;

  if (/^(https?:\/\/|\/(?!\/)|data:image\/(png|jpe?g|webp|gif|avif);base64,|blob:)/i.test(src)) {
    return src;
  }

  return undefined;
}

function parseImageDimension(value: unknown, fallback: number) {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return Math.round(value);
  if (typeof value !== 'string') return fallback;

  const match = value.trim().match(/^(\d+(?:\.\d+)?)px$/);
  if (!match) return fallback;

  const parsed = Number(match[1]);
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : fallback;
}

function findCollectionForPage(data: MenuData, document: PagedFlipDocument, page: PagedFlipPage): MenuCollection | undefined {
  const explicitCollection = (data.menu.collections || []).find((entry) => entry.id === page.chapterId);
  if (explicitCollection) {
    return explicitCollection;
  }

  if (!page.chapterId) {
    return undefined;
  }

  const chapter = document.chapters.find((entry) => entry.id === page.chapterId);
  return {
    id: page.chapterId,
    name: page.chapterLabel || chapter?.label || data.menu.title || 'Menu',
    slug: page.chapterId,
    description: chapter?.description || data.menu.description,
    is_default: true,
    categories: data.menu.categories || [],
    items: data.menu.items || [],
    featuredItems: data.menu.featuredItems || [],
    sections: data.menu.sections || [],
    categoriesBySection: data.menu.categoriesBySection,
  };
}

function findPrimaryCategory(data: MenuData, page: PagedFlipPage) {
  const blockCategory = page.categories?.[0];
  const blockFallback = blockCategory
    ? {
        id: blockCategory.sourceCategoryId || blockCategory.id,
        name: blockCategory.name,
        description: blockCategory.description,
        image: blockCategory.image,
        items: blockCategory.items,
      }
    : undefined;

  if (blockFallback) return blockFallback;

  return page.spotlightCategoryId
    ? (data.menu.categories || []).find((entry) => entry.id === page.spotlightCategoryId)
    : undefined;
}

function findResolvedSection(
  data: MenuData,
  collection: MenuCollection | undefined,
  page: PagedFlipPage,
  category: ReturnType<typeof findPrimaryCategory>,
): MenuSection | undefined {
  const categorySectionId =
    category && 'section_id' in category ? category.section_id : undefined;

  return (
    (categorySectionId
      ? collection?.sections?.find((entry) => entry.id === categorySectionId) ||
        data.menu.sections?.find((entry) => entry.id === categorySectionId)
      : undefined) ||
    collection?.sections?.find((entry) => entry.name === page.eyebrow || entry.id === page.eyebrow) ||
    data.menu.sections?.find((entry) => entry.name === page.eyebrow || entry.id === page.eyebrow)
  );
}

function buildPageContext({
  template,
  templateResolvedVariables,
  data,
  document,
  page,
  onJumpToChapter,
  runtime,
}: {
  template: Template;
  templateResolvedVariables?: Record<string, any>;
  data: MenuData;
  document: PagedFlipDocument;
  page: PagedFlipPage;
  onJumpToChapter?: (chapterId: string) => void;
  runtime?: {
    config?: Record<string, any>;
    isMobile?: boolean;
    mode?: TemplateThemeMode;
    palette?: Record<string, any>;
    typography?: Record<string, any>;
  };
}) {
  const chapterIndex = document.chapters.findIndex((entry) => entry.id === page.chapterId);
  const baseChapter = chapterIndex >= 0 ? document.chapters[chapterIndex] : undefined;
  const collection = findCollectionForPage(data, document, page);
  const resolvedCategory = findPrimaryCategory(data, page);
  const resolvedSection = findResolvedSection(data, collection, page, resolvedCategory);
  const chapterPages = page.chapterId
    ? document.pages.filter((entry) => entry.chapterId === page.chapterId)
    : [];
  const pageIndex = document.pages.findIndex((entry) => entry.id === page.id);
  const pageItems = collectPageItems(page);
  const pageKindLabel = getPageKindLabel(page.kind);
  const resolvedConfig = runtime?.config || template.pagedFlip || {};
  const resolvedSections = collection?.sections || data.menu.sections || [];
  const resolvedCollections = data.menu.collections || [];
  const pagedFlipRuntime = {
    config: resolvedConfig,
    isMobile: Boolean(runtime?.isMobile),
    themeMode: runtime?.mode || 'light',
    palette: runtime?.palette || {},
    typography: runtime?.typography || {},
    pageKindLabel,
    pageLabel: page.eyebrow || page.chapterLabel || pageKindLabel,
    features: {
      showItemDescriptions: resolvedConfig.showItemDescriptions !== false,
      showPromotionBadges: resolvedConfig.showPromotionBadges !== false,
      showCategoryImages: resolvedConfig.showCategoryImages !== false,
      showItemImages: resolvedConfig.showCategoryImages !== false,
      showCategorySpotlights: resolvedConfig.showCategorySpotlights !== false,
      showSectionDividerPages: resolvedConfig.showSectionDividerPages !== false,
      showFeaturedItemPages: resolvedConfig.showFeaturedItemPages !== false,
      showNewItemPages: resolvedConfig.showNewItemPages !== false,
      chapterIntroPages: resolvedConfig.chapterIntroPages !== false,
      showTableOfContents: resolvedConfig.showTableOfContents !== false,
      showBackCover: resolvedConfig.showBackCover !== false,
      useCollectionsAsChapters: resolvedConfig.useCollectionsAsChapters !== false,
      keepCategoryTogether: Boolean(resolvedConfig.keepCategoryTogether),
      desktopSpread: resolvedConfig.desktopSpread !== false,
      mobileDisableTouchFlip: resolvedConfig.mobileDisableTouchFlip !== false,
      mobileHideCategorySpotlight: resolvedConfig.mobileHideCategorySpotlight !== false,
    },
    engine: {
      contentFlow: resolvedConfig.contentFlow || 'flow',
      contentLayout: page.layout || resolvedConfig.contentLayout || 'stacked',
      categoryRules: resolvedConfig.categoryRules || [],
      metrics: page.metrics || {},
    },
  };
  const runtimePage = {
    ...page,
    slot: page.kind,
    kindLabel: pageKindLabel,
    displayLabel: page.eyebrow || page.chapterLabel || pageKindLabel,
    index: pageIndex,
    pageIndex,
    totalPages: document.pages.length,
    isFirstPage: pageIndex === 0,
    isLastPage: pageIndex === document.pages.length - 1,
    categories: page.categories || [],
    items: pageItems,
    metaRows: page.metaRows || [],
    tocEntries: page.tocEntries || [],
    primaryCategory: resolvedCategory,
    primarySection: resolvedSection,
    chapter: baseChapter,
    collection,
    layoutMode: page.layout || resolvedConfig.contentLayout || 'stacked',
    calculations: {
      ...(page.metrics || {}),
      layout: page.layout || resolvedConfig.contentLayout || 'stacked',
      hasContinuation: Boolean(page.metrics?.continuationCount),
      fillPercent: Math.round((page.metrics?.fillRatio || 0) * 100),
    },
  };
  const chapter = baseChapter
    ? {
        ...baseChapter,
        index: chapterIndex,
        pageCount: chapterPages.length,
        pages: chapterPages,
        firstPage: chapterPages[0],
        lastPage: chapterPages[chapterPages.length - 1],
      }
    : undefined;
  const runtimeBook = {
    ...document,
    pageCount: document.pages.length,
    currentPageIndex: pageIndex,
    currentPage: runtimePage,
  };
  const runtimeTemplate = {
    ...template,
    variables: templateResolvedVariables || template.variables,
  };
  const pagePromo =
    (resolvedCategory && 'promotion' in resolvedCategory ? resolvedCategory.promotion : undefined) ||
    pageItems.find((item) => item?.promotion)?.promotion ||
    undefined;

  return {
    ...data,
    template: runtimeTemplate,
    templateResolvedVariables,
    templateConfig: template.templateConfig || {},
    page: runtimePage,
    pages: runtimeBook.pages,
    book: runtimeBook,
    chapter,
    chapters: runtimeBook.chapters,
    collection,
    collections: resolvedCollections,
    section: resolvedSection,
    sections: resolvedSections,
    category: resolvedCategory,
    categories: runtimePage.categories,
    item: pageItems[0],
    items: pageItems,
    featuredItems: collection?.featuredItems || data.menu.featuredItems || [],
    metaRows: runtimePage.metaRows,
    tocEntries: runtimePage.tocEntries,
    config: resolvedConfig,
    pagedFlip: pagedFlipRuntime,
    palette: runtime?.palette || {},
    typography: runtime?.typography || {},
    ui: {
      isMobile: Boolean(runtime?.isMobile),
    },
    promo: pagePromo,
    promotion: pagePromo,
    actions: {
      jumpToChapter: onJumpToChapter,
    },
  };
}

function renderPagedSplitColumns(
  node: TemplateNode,
  context: any,
  options: PagedNodeRenderOptions,
) {
  const splitItemsColumns = Boolean(node.props?.splitItemsColumns);
  const splitCategoriesColumns = Boolean(node.props?.splitCategoriesColumns);

  if (!splitItemsColumns && !splitCategoriesColumns) return null;

  const leftRepeater = findAllRepeaters(node).find((entry) => entry.props?.column === 0);
  const rightRepeater = findAllRepeaters(node).find((entry) => entry.props?.column === 1);

  if (!leftRepeater || !rightRepeater) return null;

  const data = splitItemsColumns
    ? context.category?.items || context.page?.items || context.menu?.items || []
    : context.page?.categories || context.menu?.categories || [];
  const [leftData, rightData] = splitColumns(data);

  const { style, className } = computeStyle(
    node,
    options.scales,
    options.scaleTexts,
    context,
    true,
  );
  const splitGap = options.isCompactSplitLayout ? 12 : 32;

  const leftContext = splitItemsColumns
    ? { ...context, category: { ...(context.category || {}), items: leftData } }
    : { ...context, page: { ...(context.page || {}), categories: leftData } };
  const rightContext = splitItemsColumns
    ? { ...context, category: { ...(context.category || {}), items: rightData } }
    : { ...context, page: { ...(context.page || {}), categories: rightData } };

  return (
    <div key={node.id} id={node.props?.id} className={clsx(className, node.props?.className)} style={{ ...style, overflow: 'hidden' }}>
      <div className="flex flex-row items-start overflow-hidden" style={{ gap: splitGap, alignItems: 'flex-start' }}>
        <div style={{ ...(leftRepeater.props?.style || {}), flex: 1, minWidth: 0, overflow: 'hidden' }}>
          {renderPagedNode(leftRepeater, leftContext, options)}
        </div>
        <div style={{ ...(rightRepeater.props?.style || {}), flex: 1, minWidth: 0, overflow: 'hidden' }}>
          {renderPagedNode(rightRepeater, rightContext, options)}
        </div>
      </div>
    </div>
  );
}

export function renderPagedNode(
  node: TemplateNode,
  context: any,
  options: PagedNodeRenderOptions = {
    scales: { font: 1, spacing: 1, layout: 1 },
    scaleTexts: true,
    isCompactSplitLayout: false,
  },
): React.ReactNode {
  const promo =
    context?.item?.promotion ||
    context?.category?.promotion ||
    context?.promo ||
    context?.parentPromo;

  if (node.type === 'modal') {
    return null;
  }

  if (node.props?.ifPromo && !promo) return null;
  if (node.props?.ifPromoType) {
    if (!promo) return null;
    const allowedTypes = Array.isArray(node.props.ifPromoType)
      ? node.props.ifPromoType
      : [node.props.ifPromoType];
    if (!allowedTypes.includes(promo?.discount_type)) return null;
  }

  if (node.props?.show === false) return null;
  if (typeof node.props?.show === 'string') {
    const showResult = resolveBindingExpression(node.props.show, {
      ...context,
      promo,
      promotion: promo,
      parentPromo: promo,
    });
    if (!showResult || showResult === 'false') return null;
  }

  const hasAvailabilityData = Boolean(
    context?.item?.availability_label ||
      (Array.isArray(context?.item?.available_Days) && context.item.available_Days.length > 0) ||
      context?.item?.available_Times?.start ||
      context?.item?.available_Times?.end,
  );
  if (node.props?.availabilityOnly && !hasAvailabilityData) return null;
  if (node.props?.hideWhenUnavailable && context?.item?.available === false) return null;

  const splitColumnsNode = renderPagedSplitColumns(
    node,
    { ...context, promo, promotion: promo, parentPromo: promo },
    options,
  );
  if (splitColumnsNode) return splitColumnsNode;

  // --- REPEATER LOGIC ---
  //
  // A repeater node renders its OWN element once, wrapping the expanded cells.
  // The wrapper carries the node's `className` (grid `sm:grid-cols-2`, `space-y-4`)
  // and its `layout` class: returning the cells bare dropped both, which is why
  // every paged-flip repeater grid was invisible. Do NOT return the cells directly.
  let prerenderedChildren: React.ReactNode[] | null = null;
  if (node.props?.repeater) {
    const repeaterData = resolveRepeaterCollection(node, context);
    const contextKey = inferRepeaterContextKey(node.props?.bindings?.repeatFor);

    const expanded = repeaterData
      .map((entry: any, index: number) =>
        (node.children || []).map((child) => {
          const repeatedNodeId = `${node.id}__${index}__${child.id}`;
          const repeatedChild = {
            ...child,
            id: repeatedNodeId,
            props: {
              ...(child.props || {}),
              id: repeatedNodeId,
              'data-repeat-root': 'true',
              'data-repeat-context': contextKey,
              'data-repeat-index': String(index),
              ...(contextKey === 'item'
                ? {
                    'data-item-id': String(entry?.id || ''),
                  }
                : {}),
              ...(contextKey === 'category'
                ? {
                    'data-category-id': String(entry?.sourceCategoryId || entry?.id || ''),
                    'data-category-continuation': entry?.continuation ? 'true' : 'false',
                  }
                : {}),
            } as any,
          };
          const injected =
            contextKey === 'category' ||
            contextKey === 'collection' ||
            contextKey === 'item' ||
            contextKey === 'section' ||
            contextKey === 'page' ||
            contextKey === 'metaRow' ||
            contextKey === 'tocEntry'
              ? injectBindings(repeatedChild, entry, contextKey, index)
              : repeatedChild;

          const nextPromo = entry?.promotion || entry?.promo || promo;
          const resolvedNextPromo =
            contextKey === 'item'
              ? entry?.promotion || entry?.promo || undefined
              : nextPromo;
          const nextContext = extendRepeaterContext(
            {
              ...context,
              promo: resolvedNextPromo,
              promotion: resolvedNextPromo,
              parentPromo: contextKey === 'item' ? undefined : promo,
            },
            contextKey,
            entry,
          );

          return renderPagedNode(injected, nextContext, options);
        }),
      )
      .flat();

    if (expanded.length === 0) return null;
    prerenderedChildren = expanded;
  }

  const bindingMap = isBindingMap(node.props?.bindings) ? node.props.bindings : {};
  const resolvedProps: Record<string, any> = resolveAllBindings(bindingMap, {
    ...context,
    promo,
    promotion: promo,
    parentPromo: promo,
  }) as Record<string, any>;

  Object.keys(resolvedProps).forEach((key) => {
    resolvedProps[key] = resolveRenderableValue(resolvedProps[key], context);
  });

  const directText = resolveRenderableValue(node.props?.text, context);
  const directChildren = resolveRenderableValue(node.props?.children, context);
  const directHref = resolveRenderableValue(node.props?.href, context);
  const directTarget = resolveRenderableValue(node.props?.target, context);
  const directAlt = resolveRenderableValue(node.props?.alt, context);
  const directSrc = resolveRenderableValue(node.props?.src, context);
  const directValue = resolveRenderableValue(node.props?.value, context);

  let imagePresentation: ReturnType<typeof resolveImagePresentation> | undefined;
  let cutoutShapeOverride: string | undefined;
  if (node.type === 'img') {
    let imageSource = (resolvedProps.src ?? directSrc) as string | undefined;
    if (node.props?.imageBinding) {
      imageSource =
        (resolvePath(context, node.props.imageBinding) as string | undefined) ||
        (directSrc as string | undefined);
    }
    const entry = (context.item ?? context.category) as Record<string, unknown> | undefined;
    const cut = resolveCutoutSource(node.props?.cutout, entry, imageSource);
    resolvedProps.src = cut.src;
    cutoutShapeOverride = cut.shapeOverride;
    if (node.props?.decor && typeof resolvedProps.src === 'string') {
      resolvedProps.src = resolveDecorAssetUrl(resolvedProps.src) ?? resolvedProps.src;
    }
    imagePresentation = resolveImagePresentation({
      shape: cutoutShapeOverride ?? node.props?.shape,
      ring: node.props?.ring,
      shadow: node.props?.shadow,
    });
  }

  const animationClass = node.props?.animate ? `menuraq-anim-${node.props.animate}` : undefined;
  const rawClassName = resolveClassNameBindings(`${node.props?.className || ''} ${animationClass || ''}`, context);
  const boundClassName = resolveClassName(rawClassName, context);
  const { className, style } = computeStyle(
    node,
    options.scales,
    options.scaleTexts,
    { ...context, promo, promotion: promo, parentPromo: promo },
    true,
  );
  const elementProps = sanitizeElementProps(node);
  const text = applyTextUtils(
    pickRenderableText(resolvedProps.text, resolvedProps.children, directText, directChildren),
    node,
  );

  const children =
    prerenderedChildren ??
    (Array.isArray(node.children)
      ? node.children.map((child) =>
          renderPagedNode(
            child,
            { ...context, promo, promotion: promo, parentPromo: promo },
            options,
          ))
      : null);

  let mergedClassName = clsx(boundClassName, className);
  let renderedStyle = applyClassTextScale(style, mergedClassName, options);

  const groupLayout = node.props?.layout ? resolveGroupLayout(node.props.layout) : undefined;
  if (groupLayout) {
    if (groupLayout.className) mergedClassName = `${mergedClassName} ${groupLayout.className}`.trim();
    if (groupLayout.style) Object.assign(renderedStyle, groupLayout.style);
  }
  const chapterTarget = resolvedProps.targetId ?? node.props?.targetId;
  const handleClick =
    node.props?.hotspot === 'chapter' &&
    typeof chapterTarget === 'string' &&
    chapterTarget.trim().length > 0 &&
    typeof context?.actions?.jumpToChapter === 'function'
      ? (event?: React.MouseEvent<HTMLElement>) => {
          event?.preventDefault();
          context.actions.jumpToChapter(chapterTarget);
        }
      : undefined;

  switch (node.type) {
    case 'img':
      const imageSrc = normalizeImageSrc(resolvedProps.src ?? directSrc ?? node.props?.src);
      if (!imageSrc) return null;

      const imageConfig = context?.config?.images || {};
      const prioritySlots = Array.isArray(imageConfig.prioritySlots) ? imageConfig.prioritySlots : ['cover'];
      const isPriorityImage = prioritySlots.includes(context?.page?.kind);
      const rawNodeProps = node.props as Record<string, unknown> | undefined;
      const imageWidth = parseImageDimension(style.width ?? resolvedProps.width ?? rawNodeProps?.width, 320);
      const imageHeight = parseImageDimension(style.height ?? resolvedProps.height ?? rawNodeProps?.height, 240);
      const imageLoading = isPriorityImage || imageConfig.loading === 'eager' ? 'eager' : 'lazy';
      const imageQuality =
        typeof imageConfig.quality === 'number' && Number.isFinite(imageConfig.quality)
          ? Math.min(Math.max(Math.round(imageConfig.quality), 1), 100)
          : 80;
      const imageSizes =
        typeof imageConfig.sizes === 'string' && imageConfig.sizes.trim()
          ? imageConfig.sizes
          : '(max-width: 768px) 100vw, 50vw';

      return (
        <div
          {...elementProps}
          key={node.id}
          className="relative overflow-hidden"
          aria-hidden={node.props?.decor ? true : undefined}
          style={{
            // Only set inline dimensions when the template explicitly provides them.
            // If the template relies on Tailwind classes (e.g. w-16 h-16), omitting
            // these lets the class-based sizing win instead of the numeric defaults.
            ...(style.width ? { width: style.width } : {}),
            ...(style.height ? { height: style.height } : {}),
            ...(node.props?.decor ? { pointerEvents: 'none' } : {}),
          }}
        >
          <Image
            className={clsx(mergedClassName, imagePresentation?.className)}
            style={{ ...renderedStyle, ...imagePresentation?.style, objectFit: renderedStyle.objectFit || imageConfig.fit || 'cover' }}
            width={imageWidth}
            height={imageHeight}
            src={imageSrc}
            alt={node.props?.decor ? '' : (resolvedProps.alt ?? directAlt) || ''}
            loading={imageLoading}
            priority={isPriorityImage}
            quality={imageQuality}
            sizes={imageSizes}
            unoptimized={imageConfig.optimize === 'never' || imageSrc.startsWith('data:') || imageSrc.startsWith('blob:')}
          />
        </div>
      );

    case 'button':
      return (
        <button
          {...elementProps}
          key={node.id}
          className={mergedClassName}
          style={{ ...renderedStyle }}
          disabled={node.props?.disabled}
          onClick={handleClick}
        >
          {renderTextOrChildren(text, children)}
        </button>
      );
    case 'a':
      return (
        <Link
          {...elementProps}
          key={node.id}
          className={mergedClassName}
          style={{ ...renderedStyle }}
          href={resolvedProps.href ?? directHref}
          target={resolvedProps.target ?? directTarget}
          onClick={handleClick}
        >
          {renderTextOrChildren(text, children)}
        </Link>
      );
    case 'input':
      return (
        <input
          {...elementProps}
          key={node.id}
          className={mergedClassName}
          style={{ ...renderedStyle }}
          value={resolvedProps.value ?? directValue}
        />
      );
    case 'textarea':
      return (
        <textarea
          {...elementProps}
          key={node.id}
          className={mergedClassName}
          style={{ ...renderedStyle }}
          value={resolvedProps.value ?? directValue}
        />
      );
    case 'select':
      return (
        <select {...elementProps} key={node.id} className={mergedClassName} style={{ ...renderedStyle }} onClick={handleClick}>
          {children}
        </select>
      );
    case 'option':
      return (
        <option {...elementProps} key={node.id} className={mergedClassName} style={{ ...renderedStyle }}>
          {renderTextOrChildren(text, children)}
        </option>
      );
    case 'li':
      return (
        <li {...elementProps} key={node.id} className={mergedClassName} style={{ ...renderedStyle }} onClick={handleClick}>
          {renderTextOrChildren(text, children)}
        </li>
      );
    case 'video':
      return (
        <video
          {...elementProps}
          key={node.id}
          className={mergedClassName}
          style={{ ...renderedStyle }}
          src={resolvedProps.src ?? directSrc}
          onClick={handleClick}
        >
          {children}
        </video>
      );
    case 'audio':
      return (
        <audio
          {...elementProps}
          key={node.id}
          className={mergedClassName}
          style={{ ...renderedStyle }}
          src={resolvedProps.src ?? directSrc}
          onClick={handleClick}
        >
          {children}
        </audio>
      );
    case 'iframe':
      return (
        <iframe
          {...elementProps}
          key={node.id}
          className={mergedClassName}
          style={{ ...renderedStyle }}
          src={resolvedProps.src ?? directSrc}
          onClick={handleClick}
        />
      );
    case 'ul':
    case 'ol':
    case 'table':
    case 'thead':
    case 'tbody':
    case 'tr':
    case 'td':
    case 'th':
    case 'div':
    case 'section':
    case 'span':
    case 'h1':
    case 'h2':
    case 'h3':
    case 'h4':
    case 'h5':
    case 'h6':
    case 'p':
    case 'label':
    case 'figcaption':
    case 'footer':
    case 'header':
    case 'nav':
    case 'main':
    case 'aside':
    case 'article':
    case 'figure':
    case 'form':
      return React.createElement(
        node.type,
        {
          ...elementProps,
          key: node.id,
          className: mergedClassName,
          style: { ...renderedStyle },
          onClick: handleClick,
        },
        renderTextOrChildren(text, children),
      );
    default:
      return React.createElement(
        node.type,
        {
          ...elementProps,
          key: node.id,
          className: mergedClassName,
          style: { ...renderedStyle },
          onClick: handleClick,
        },
        children,
      );
  }
}

export function renderFlipPageTemplate({
  template,
  templateResolvedVariables,
  data,
  document,
  page,
  onJumpToChapter,
  config,
  isMobile,
  mode,
  palette,
  typography,
  scales,
  scaleTexts = true,
  isCompactSplitLayout,
}: {
  template: Template;
  templateResolvedVariables?: Record<string, any>;
  data: MenuData;
  document: PagedFlipDocument;
  page: PagedFlipPage;
  onJumpToChapter?: (chapterId: string) => void;
  config?: Record<string, any>;
  isMobile?: boolean;
  mode?: TemplateThemeMode;
  palette?: Record<string, any>;
  typography?: Record<string, any>;
  scales?: PagedNodeRenderOptions['scales'];
  scaleTexts?: boolean;
  isCompactSplitLayout?: boolean;
}) {
  if (page.kind === 'blank') return null;

  const explicitSlotNodes = getTemplatePagedNodes(template, page.kind);
  const slotNodes = explicitSlotNodes.length > 0 ? explicitSlotNodes : getDefaultPagedFlipSlotNodes(page.kind);

  if (!Array.isArray(slotNodes) || slotNodes.length === 0) return null;

  const pageContext = buildPageContext({
    template,
    templateResolvedVariables,
    data,
    document,
    page,
    onJumpToChapter,
    runtime: {
      config,
      isMobile,
      mode,
      palette,
      typography,
    },
  });
  return slotNodes.map((node) => 
    renderPagedNode(node, pageContext, {
      scales: scales || { font: 1, spacing: 1, layout: 1 },
      scaleTexts,
      isCompactSplitLayout,
    }),
  );
}
